import { useEffect } from 'react';
import { useSocket } from '../lib/socket';
import { useSms } from '../store/sms';
import type {
  SmsMessageReceivedPayload,
  SmsMessageSentPayload,
  SmsMessageStatusPayload,
  SmsConversationUpdatedPayload,
} from '@marnic/shared';

export function useSmsSocket(): void {
  const socket = useSocket();
  const addInboundMessage   = useSms((s) => s.addInboundMessage);
  const addOutboundMessage  = useSms((s) => s.addOutboundMessage);
  const updateMessageStatus = useSms((s) => s.updateMessageStatus);
  const updateConversation  = useSms((s) => s.updateConversation);

  useEffect(() => {
    if (!socket) return;

    const onMessage = (p: SmsMessageReceivedPayload) => {
      addInboundMessage(p.conversationId, p.message);
    };
    const onSent = (p: SmsMessageSentPayload) => {
      addOutboundMessage(p.conversationId, p.message);
    };
    const onStatus = (p: SmsMessageStatusPayload) => {
      updateMessageStatus(p.conversationId, p.messageId, {
        status: p.status,
        deliveredAt: p.deliveredAt ?? null,
        readAt: p.readAt ?? null,
        errorCode: p.errorCode ?? null,
      });
    };
    const onConvUpdated = (p: SmsConversationUpdatedPayload) => {
      updateConversation(p.conversation);
    };

    socket.on('sms_message_received', onMessage);
    socket.on('sms_message_sent', onSent);
    socket.on('sms_message_status', onStatus);
    socket.on('sms_conversation_updated', onConvUpdated);

    return () => {
      socket.off('sms_message_received', onMessage);
      socket.off('sms_message_sent', onSent);
      socket.off('sms_message_status', onStatus);
      socket.off('sms_conversation_updated', onConvUpdated);
    };
  }, [socket, addInboundMessage, addOutboundMessage, updateMessageStatus, updateConversation]);
}
